import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import {useEvent} from 'Event/EventProvider'
import {Question} from 'Event/Question'
import Dialog from 'lib/ui/Dialog'
import {Form} from 'organization/Event/FormsProvider'
import React from 'react'
import {DEFAULT_MODAL_BUTTON_TEXT} from 'Event/Dashboard/components/BlogPosts/BlogPostConfig'
import Box from '@material-ui/core/Box'
import {useAttendeeVariables} from 'Event'
import {BlogPost, FORM} from 'Event/Dashboard/components/BlogPosts'
import styled from 'styled-components'
import {
  DEFAULT_RESUBMIT_LABEL,
  DEFAULT_SUBMITTED_MESSAGE,
  DEFAULT_SUBMIT_LABEL,
} from 'organization/Event/Form'
import {useTemplate} from 'Event/TemplateProvider'
import {useSubmitForm} from 'Event/form'
import {useCheckQuestionRules} from 'Event/Question/use-is-visible-question'
import PostFormSubmitButton from 'Event/Dashboard/components/BlogPosts/PostFormSubmitButton'

export default function PostForm(props: {post: BlogPost}) {
  const {post} = props
  const {event} = useEvent()

  const form = event.forms.find((f) => f.id === post.formId)
  if (!form) {
    return null
  }

  return <Content form={form} post={post} />
}

function Content(props: {form: Form; post: BlogPost}) {
  const {form, post} = props
  const v = useAttendeeVariables()

  const {postFormStyles: formStyles} = useTemplate()

  const {
    openDialog,
    dialogVisible,
    resubmit,
    isEditing,
    alreadySubmitted,
    submit,
    answers,
    responseError,
    submitting,
    closeDialog,
    register,
    handleSubmit,
    errors: formErrors,
    control,
    setValue,
    watch,
  } = useSubmitForm(form, {
    showInDialog: post.isModalForm,
  })

  const checkQuestionRules = useCheckQuestionRules({watch, answers})

  const showing = post.attachment === FORM

  if (!showing) {
    return null
  }

  const body = (
    <Container justifyContent={formStyles.position}>
      <StyledForm
        onSubmit={handleSubmit(submit)}
        width={formStyles.width}
        onClick={(e) => e.stopPropagation()}
      >
        {form.questions
          .filter((question) => checkQuestionRules(question))
          .map((question, index) => (
            <Question
              formErrors={formErrors}
              key={question.id}
              index={index}
              control={control}
              question={question}
              answers={answers}
              register={register}
              responseError={responseError}
              setValue={setValue}
              disabled={submitting}
              inputStyles={formStyles.inputStyles}
              watch={watch}
            />
          ))}
        <Container justifyContent={formStyles.buttonPosition}>
          <PostFormSubmitButton
            postFormStyles={formStyles}
            disabled={submitting}
          >
            {v(form.submit_label || DEFAULT_SUBMIT_LABEL)}
          </PostFormSubmitButton>
        </Container>
      </StyledForm>
    </Container>
  )

  const submittedMessage = <SubmittedMessage resubmit={resubmit} form={form} />

  const canSubmitAgain = alreadySubmitted && !isEditing

  const content = canSubmitAgain ? submittedMessage : body

  if (post.isModalForm) {
    return (
      <PostFormDialog
        open={dialogVisible}
        onClose={closeDialog}
        onOpen={openDialog}
        form={form}
        post={post}
      >
        {content}
      </PostFormDialog>
    )
  }

  return content
}

function PostFormDialog(props: {
  children: JSX.Element
  open: boolean
  onClose: () => void
  onOpen: () => void
  form: Form
  post: BlogPost
}) {
  const {open, onClose, form, children, onOpen, post} = props
  const v = useAttendeeVariables()
  const {postFormStyles: formStyles} = useTemplate()

  const button = (
    <Container
      justifyContent={formStyles.buttonPosition}
      onClick={(e) => e.stopPropagation()}
    >
      <PostFormSubmitButton postFormStyles={formStyles} onClick={onOpen}>
        {v(post.modalButtonText) || DEFAULT_MODAL_BUTTON_TEXT}
      </PostFormSubmitButton>
    </Container>
  )

  // To prevent dialog content flash, we'll just NOT return the dialog
  // at all when it's hidden.
  if (!open) {
    return button
  }

  return (
    <>
      <Dialog open onClose={onClose}>
        <DialogTitle>{form.name}</DialogTitle>
        <DialogContent>
          <Box pb={2}>{children}</Box>
        </DialogContent>
      </Dialog>
      {button}
    </>
  )
}

function SubmittedMessage(props: {resubmit: () => void; form: Form}) {
  const v = useAttendeeVariables()

  const {postFormStyles: formStyles, postStyles} = useTemplate()

  if (!props.form.can_resubmit) {
    return (
      <FormSubmittedMessage textColor={postStyles.contentTextColor}>
        {v(props.form.submitted_message || DEFAULT_SUBMITTED_MESSAGE)}
      </FormSubmittedMessage>
    )
  }

  return (
    <div>
      <FormSubmittedMessage textColor={postStyles.contentTextColor}>
        {v(props.form.submitted_message || DEFAULT_SUBMITTED_MESSAGE)}
      </FormSubmittedMessage>
      <Container justifyContent={formStyles.buttonPosition}>
        <PostFormSubmitButton
          postFormStyles={formStyles}
          onClick={props.resubmit}
        >
          {v(props.form.resubmit_button_label || DEFAULT_RESUBMIT_LABEL)}
        </PostFormSubmitButton>
      </Container>
    </div>
  )
}

const StyledForm = styled.form<{
  width: number
}>`
  width: ${(props) => props.width}%;
`

const Container = styled.div<{
  justifyContent: string
}>`
  display: flex;
  justify-content: ${(props) => props.justifyContent};
`
const FormSubmittedMessage = styled.p<{textColor: string | null}>`
  color: ${(props) => props.color};
`
